import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { getUserPermissions } from 'src/app/store/selectors/user.selector';
import { AppState } from 'src/app/store/states/app.state';
import { PermissionType } from '../models/permission-type.enum';
import { AuthGuard } from './auth.guard';

@Injectable({
  providedIn: 'root'
}) export class PermissionGuard {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private authGuard: AuthGuard
  ) { }

  static canActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const permissionGuard = inject(PermissionGuard);
    return permissionGuard.checkUserPermission(state.url, route);
  }

  checkUserPermission(returnUrl: string, route: ActivatedRouteSnapshot): Observable<boolean> {

    return this.authGuard.checkUserAuth(returnUrl).pipe(switchMap(isUserAuthenticated => {
      if (isUserAuthenticated) {
        return this.store.select(getUserPermissions).pipe(
          map(permissions => {
            let isAllowed = false
            switch (route.routeConfig?.path) {
              case 'calendar':
                isAllowed = permissions.includes(PermissionType.MANAGE_APPOINTMENTS) || permissions.includes(PermissionType.VIEW_APPOINTMENTS);
                break;
              case 'medical-history':
                isAllowed = permissions.includes(PermissionType.MANAGE_MEDICAL_HISTORY) || permissions.includes(PermissionType.VIEW_MEDICAL_HISTORY) || permissions.includes(PermissionType.MANAGE_ORG_UNIT);
                break;
              case 'users':
                isAllowed = permissions.includes(PermissionType.MANAGE_USERS) || permissions.includes(PermissionType.VIEW_USERS) || permissions.includes(PermissionType.VIEW_ROLES) || permissions.includes(PermissionType.MANAGE_ROLES) || permissions?.includes(PermissionType.MANAGE_DIANA);
                break;
              case 'customer':
                isAllowed = permissions.includes(PermissionType.MANAGE_CUSTOMER) || permissions.includes(PermissionType.VIEW_CUSTOMER);
                break;
              case 'tenants':
                isAllowed = permissions.includes(PermissionType.MANAGE_DIANA) || permissions.includes(PermissionType.MANAGE_TENANT) || permissions.includes(PermissionType.VIEW_TENANT);
                break;
              case 'org-units':
                isAllowed = permissions.includes(PermissionType.MANAGE_DIANA) || permissions.includes(PermissionType.VIEW_ORG_UNIT) || permissions.includes(PermissionType.MANAGE_ORG_UNIT);
                break;
              case 'reporting':
                isAllowed = permissions.includes(PermissionType.MANAGE_DIANA) || permissions.includes(PermissionType.MANAGE_MEDICAL_HISTORY);
                break;
              case 'settings':
                isAllowed = permissions.includes(PermissionType.MANAGE_DIANA) || permissions.includes(PermissionType.VIEW_SETTING) || permissions.includes(PermissionType.MANAGE_SETTING);
                break;
              case 'add-customer':
                isAllowed = permissions.includes(PermissionType.MANAGE_NEW_CUSTOMER);
                break;
              default:
                break;
            }

            if (isAllowed) {
              return isAllowed;
            } else if (route.routeConfig?.path == 'calendar') {
              return this.redirectToHome();
            } else {
              return this.redirectToCalendar();
            }
          })
        );
      }
      else {
        return of(this.redirectToHome());
      }
    }));
  }

  private redirectToHome() {
    this.router.navigate(['/home']);
    return true;
  }

  private redirectToCalendar() {
    this.router.navigate(['/calendar']);
    return true;
  }

}
